import React, { useEffect, useState } from 'react';
import './invoicebill.css';
import ThreeDot from '../../assets/icons/threedot.svg';
import Pdf from '../../assets/icons/pdf.svg';
import View from '../../assets/icons/view.svg';
import Download from '../../assets/icons/download.svg';
import Edit from '../../assets/icons/edit.svg';
import folder_icon from '../../assets/images/folder_icon.png'
import { useDownloadInvoiceQuery, useMasterDetailsQuery } from '../../slices/Profile';
import { InvoiceForm } from '../Modal/invoiceForm/InvoiceForm';

const InvoiceBill = () => {
    const { data: masterData, refetch: masterDataRefetch } = useMasterDetailsQuery();
    const [invoice, setInvoice] = useState(false)
    const [invoiceId, setInvoiceId] = useState('')
    const [invoiceType, setInvoiceType] = useState('')
    const [downloadType, setDownloadType] = useState(null);
    const { data: downloadFile, refetch } = useDownloadInvoiceQuery({ invoice_id: invoiceId, download_type: downloadType }, { skip: !invoiceId || !downloadType });
    const [expandedYear, setExpandedYear] = useState(null); // Track which year's invoices are expanded
    const [showIcons, setShowIcons] = useState(null); // Track which card's icons are showing
    const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(null);
    const [selectedInvoices, setSelectedInvoices] = useState(new Set());
    const [bulkDownloading, setBulkDownloading] = useState(false);

    const toggleSelect = (invoice_id) => {
        setSelectedInvoices(prev => {
            const next = new Set(prev);
            next.has(invoice_id) ? next.delete(invoice_id) : next.add(invoice_id);
            return next;
        });
    };

    const handleBulkDownload = async (type) => {
        setBulkDownloading(true);
        const invoicesToDownload = allInvoices.filter(inv => selectedInvoices.has(inv.invoice_id));
        for (const inv of invoicesToDownload) {
            const file = await fetchDownloadFile(inv.invoice_id, type);
            if (file) {
                const url = window.URL.createObjectURL(new Blob([file]));
                const link = document.createElement('a');
                link.href = url;
                const fy = getFinancialYearShort(inv.invoice_date);
                link.setAttribute('download', `Invoice ${inv.invoice_no} (${fy}).pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                await new Promise(res => setTimeout(res, 300));
            }
        }
        setBulkDownloading(false);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.three_dot_icon')) {
                setShowIcons(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Flat list of all invoices for prev/next navigation
    const allInvoices = masterData?.data?.invoice_entries ?? [];

    // Group invoices by year
    // const invoicesByYear = masterData?.data?.invoice_entries.reduce((acc, invoice) => {
    //     const year = new Date(invoice.invoice_date).getFullYear();
    //     if (!acc[year]) {
    //         acc[year] = [];
    //     }
    //     acc[year].push(invoice);
    //     return acc;
    // }, {});
    const invoicesByYear = masterData?.data?.invoice_entries.reduce((acc, invoice) => {
        const date = new Date(invoice.invoice_date);
        const year = date.getFullYear();
        const month = date.getMonth(); // 0-indexed: Jan = 0, Apr = 3
    
        // Determine the financial year label
        const financialYearStart = (month >= 3) ? year : year - 1;
        const financialYearEnd = financialYearStart + 1;
        const financialYearLabel = `${financialYearStart}-${financialYearEnd}`;
    
        // Group by financial year
        if (!acc[financialYearLabel]) {
            acc[financialYearLabel] = [];
        }
        acc[financialYearLabel].push(invoice);
        
        return acc;
    }, {});
    
    const toggleYear = (year) => {
        setExpandedYear(expandedYear === year ? null : year); // Toggle visibility of invoices for the selected year
    };

    const toggleIcons = (index) => {
        setShowIcons(showIcons === index ? null : index); // Show icons for clicked card only
    };


    const handleView = (invoice_id) => {
        const index = allInvoices.findIndex(inv => inv.invoice_id === invoice_id);
        setCurrentInvoiceIndex(index);
        setInvoice(true);
        setInvoiceId(invoice_id);
        setInvoiceType('view');
    };

    const handlePrev = () => {
        const prevIndex = currentInvoiceIndex - 1;
        if (prevIndex >= 0) {
            setCurrentInvoiceIndex(prevIndex);
            setInvoiceId(allInvoices[prevIndex].invoice_id);
        }
    };

    const handleNext = () => {
        const nextIndex = currentInvoiceIndex + 1;
        if (nextIndex < allInvoices.length) {
            setCurrentInvoiceIndex(nextIndex);
            setInvoiceId(allInvoices[nextIndex].invoice_id);
        }
    };

    const getFinancialYearShort = (dateStr) => {
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = date.getMonth();
        const start = month >= 3 ? year : year - 1;
        return `${String(start).slice(-2)}-${String(start + 1).slice(-2)}`;
    };

    const handleDownload = async (invoice_id, item, invoice_no, invoice_date) => {
        setInvoiceId(invoice_id);
        setDownloadType(item);
        try {
            const file = await fetchDownloadFile(invoice_id, item);
            if (file) {
                const url = window.URL.createObjectURL(new Blob([file], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = url;
                const fy = getFinancialYearShort(invoice_date);
                link.setAttribute('download', `Invoice ${invoice_no} (${fy}).pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } else {
                console.error('No file available for download.');
            }
        } catch (error) {
            console.error('Error fetching file:', error);
        }
    };

    const handleOpenPdf = async (invoice_id, item) => {
        try {
            const file = await fetchDownloadFile(invoice_id, item);
            if (file) {
                const url = window.URL.createObjectURL(new Blob([file], { type: 'application/pdf' }));
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error('Error opening file:', error);
        }
    };

    const fetchDownloadFile = async (invoice_id, item) => {
        const currentToken = JSON.parse(localStorage.getItem('token'));
        const headers = new Headers();
        if (currentToken) {
            headers.set('Authorization', `Bearer ${currentToken}`);
        }
        headers.set('Content-Type', 'application/json');
        headers.set('ngrok-skip-browser-warning', 'true');

        try {
            const response = await fetch(`https://api.srisendhurtex.in/api/v1/download-invoice?invoice_id=${invoice_id}&download_type=${item}`, {
                method: 'GET',
                headers: headers,
            });
            if (response.ok) {
                return await response.arrayBuffer();
            } else {
                console.error('Failed to fetch file:', response.statusText);
                return null;
            }
        } catch (error) {
            console.error('Error fetching file:', error);
            return null;
        }
    };

    const handleEdit = (invoice_id) => {
        setInvoice(true)
        setInvoiceId(invoice_id)
        setInvoiceType('edit')
    };
    console.log(masterData?.data);
    

    return (
        <>
            {!invoice && (
                <div>
                    <div className='customer_heading'>
                        <h3>Invoice Bill</h3>
                    </div>

                    {selectedInvoices.size > 0 && (
                        <div className='bulk_action_bar'>
                            <span>{selectedInvoices.size} selected</span>
                            <button
                                className='bulk_btn'
                                onClick={() => handleBulkDownload(1)}
                                disabled={bulkDownloading}
                            >
                                {bulkDownloading ? 'Downloading...' : `Download Original (${selectedInvoices.size})`}
                            </button>
                            <button
                                className='bulk_btn bulk_btn_duplicate'
                                onClick={() => handleBulkDownload(2)}
                                disabled={bulkDownloading}
                            >
                                {bulkDownloading ? 'Downloading...' : `Download Duplicate (${selectedInvoices.size})`}
                            </button>
                            <button
                                className='bulk_btn_clear'
                                onClick={() => setSelectedInvoices(new Set())}
                                disabled={bulkDownloading}
                            >
                                Clear
                            </button>
                        </div>
                    )}
                    <div className='pdf_design'>
                        {masterData?.data?.invoice_entries.length > 0 ?
                            <>
                                {invoicesByYear && Object.keys(invoicesByYear).map((year) => (
                                    <div key={year} className='year_folder'>
                                        <div className='year_heading' onClick={() => toggleYear(year)}>
                                            <img className='folder_icon' src={folder_icon} alt='folder_icon' />
                                            <h4 className='folder_year'>{year}</h4>
                                        </div>
                                        {expandedYear === year && (
                                            <div className='invoices_list'>
                                                {invoicesByYear[year].map((value, index) => (
                                                    <div key={index} className='pdf_card'>
                                                        <input
                                                            type='checkbox'
                                                            className='invoice_checkbox'
                                                            checked={selectedInvoices.has(value?.invoice_id)}
                                                            onChange={() => toggleSelect(value?.invoice_id)}
                                                            onClick={e => e.stopPropagation()}
                                                        />
                                                        <div className='pdf_image'>
                                                            <img src={Pdf} alt='Pdf' />
                                                        </div>
                                                        <div className='pdf_each_card'>
                                                            <div>
                                                                <p>Invoice {value?.invoice_no}.pdf</p>
                                                                <p><span>{value?.invoice_date}</span></p>
                                                            </div>
                                                            <div onClick={() => toggleIcons(index)} className="three_dot_icon">
                                                                <img src={ThreeDot} alt='ThreeDot' />
                                                                {showIcons === index && (
                                                                    <div className='action_icons'>
                                                                        <div className='action_type' onClick={() => handleView(value?.invoice_id)}>
                                                                            <img src={View} alt='View' />View
                                                                        </div>
                                                                        <div className='action_type' onClick={() => handleEdit(value?.invoice_id)}>
                                                                            <img src={Edit} alt='Edit' />Edit
                                                                        </div>
                                                                        <div className='action_type' onClick={() => handleOpenPdf(value?.invoice_id, 1)}>
                                                                            <img src={View} alt='Open' />Open Original
                                                                        </div>
                                                                        <div className='action_type' onClick={() => handleOpenPdf(value?.invoice_id, 2)}>
                                                                            <img src={View} alt='Open' />Open Duplicate
                                                                        </div>
                                                                        <div className='action_type' onClick={() => handleDownload(value?.invoice_id, 1, value?.invoice_no, value?.invoice_date)}>
                                                                            <img src={Download} alt='Download' />Download Original
                                                                        </div>
                                                                        <div className='action_type' onClick={() => handleDownload(value?.invoice_id, 2, value?.invoice_no, value?.invoice_date)}>
                                                                            <img src={Download} alt='Download' />Download Duplicate
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </>
                            :
                            <p className='no_data'>No invoices available</p>
                        }
                    </div>

                </div>
            )}
            {invoice && (
                <InvoiceForm
                    invoiceId={invoiceId}
                    invoiceType={invoiceType}
                    setInvoice={setInvoice}
                    onPrev={handlePrev}
                    onNext={handleNext}
                    hasPrev={currentInvoiceIndex > 0}
                    hasNext={currentInvoiceIndex < allInvoices.length - 1}
                />
            )}
        </>
    );
};

export default InvoiceBill;
